import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, Divider, Typography, Button, Stack, FormGroup, FormHelperText } from '@mui/material';
import { useState, useCallback, useEffect } from 'react';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllSysProducts } from 'src/shared/services/sysProductService';
import { deleteUserById, getUserById, updateUser } from 'src/shared/services/userService';
import * as yup from 'yup';
import { User } from 'src/shared/models/user';
import { InfoRounded } from '@mui/icons-material';
import { Colors } from 'src/shared/components/styles/colors';
import { useGlobals } from 'src/shared/contexts/appContext';
import { SysProductYvNameEnum } from 'src/shared/models/enums/sysProductEnums';
import { District } from 'src/shared/models/district/district';
import { Association } from 'src/shared/models/association/association';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { RegexHelper } from 'src/shared/helpers/regexHelper';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { FormErrorSummary } from 'src/shared/components/form/formErrorSummary';
import { FormCharacterCount } from 'src/shared/components/form/formCharacterCount';
import { SysProductResponse } from '../models/sysProductResponse';
import { USER_COMMENT_MAX_LENGTH, UserRequest } from '../models/userRequest';
import { UserCreateModalPermissionButton } from './userCreateModalPermissionButton';
import { UserEditConfirmDelete } from './userEditConfirmDelete';
import { UserEditMembershipSummary } from './userEditMembershipSummary';
import { UserEditInvoiceGroupSummary } from './userEditInvoiceGroupSummary';

const excludedSysProducts = [
  SysProductYvNameEnum.DocumentArchive.toLowerCase(),
  SysProductYvNameEnum.ImageArchive.toLowerCase(),
  SysProductYvNameEnum.Recruit.toLowerCase(),
  SysProductYvNameEnum.Organisation.toLowerCase(),
  SysProductYvNameEnum.WebTool.toLowerCase(),
  SysProductYvNameEnum.Settings.toLowerCase(),
];

type UserEditModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  userId: number | undefined;
};

export const UserEditModal = (props: UserEditModalProps) => {
  const { modalOpen, onSave, onClose, userId } = props;

  const { t } = useTranslation(['common', 'user', 'member']);
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User>();
  const [authority, setAuthority] = useState<number>(0);
  const [loadingUser, setLoadingUser] = useState<boolean>(false);
  const [sysProductLoaded, setSysProductLoaded] = useState<boolean>(false);
  const [sysProducts, setSysProducts] = useState<SysProductResponse[]>([]);
  const [disableSysProducts, setDisableSysProducts] = useState<SysProductResponse[]>([]);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState<boolean>(false);
  const [showAssociationDropdown, setShowAssociationDropdown] = useState<boolean>(false);
  const [initialInvoiceGroup, setInitialInvoiceGroup] = useState<InvoiceGroup[] | []>([]);

  const onHandleClose = () => {
    onClose();
    setSysProductLoaded(false);
  };

  const onHandleSave = useCallback(() => {
    onSave();
    setSysProductLoaded(false);
  }, [onSave]);

  const requiredMessage = t('common:validationMessages.required');
  const loginMethodMessage = t('user:createUser.loginMethodRequired');
  const personnummerFormatMessage = t('user:createUser.personalIdentityNrFormat');
  const personNrFormat = RegexHelper.personalNumberWithDash;

  // 'authority' valideras beroende på värdet av 'isChecked'.
  const userRequestSysProductSchema = yup.object().shape({
    product_id: yup.number().min(1, requiredMessage).required(requiredMessage),
    authority: yup
      .number()
      .min(0, requiredMessage)
      .test('authority-condition.editUser', requiredMessage, (value, context) => {
        const { isChecked } = context.parent;
        if (isChecked) {
          return value !== undefined && value >= 1;
        }
        return value === undefined || value === 0;
      }),
    isChecked: yup.boolean().required(),
  });

  const atLeastOne = (selfKey: 'emailLoginEnabled' | 'bankIdLoginEnabled', otherKey: 'emailLoginEnabled' | 'bankIdLoginEnabled') => ({
    name: `at-least-one-${selfKey}`,
    message: loginMethodMessage,
    test(this: yup.TestContext, value: boolean, ctx: yup.TestContext<any>) {
      const other = (ctx.parent as any)?.[otherKey];
      return value || other || ctx.createError({ path: selfKey, message: loginMethodMessage });
    },
  });

  const CreateUserSchema: yup.Schema<UserRequest> = yup.object({
    firstName: yup.string().required(requiredMessage),
    lastName: yup.string().required(requiredMessage),
    email: yup
      .string()
      .trim()
      .matches(RegexHelper.emailAddress, {
        message: t('common:validationMessages.email'),
        excludeEmptyString: true,
      })
      .required(requiredMessage),

    comment: yup
      .string()
      .max(USER_COMMENT_MAX_LENGTH, t('common:validationMessages.maxLength', { count: USER_COMMENT_MAX_LENGTH }))
      .optional(),

    emailLoginEnabled: yup.boolean().required().default(false).test(atLeastOne('emailLoginEnabled', 'bankIdLoginEnabled')),
    bankIdLoginEnabled: yup.boolean().required().default(false).test(atLeastOne('bankIdLoginEnabled', 'emailLoginEnabled')),
    districts: yup.array<District>(),
    associations: yup.array<Association>(),

    sysProducts: yup
      .array()
      .min(1, t('common:validationMessages.required'))
      .of(userRequestSysProductSchema)
      .test('minimum-one-modul', t('user:createUser.moduleSelectionError'), (products) => {
        return products?.some((product) => product.isChecked === true);
      })
      .required(),

    invoiceGroups: yup.array<InvoiceGroup>(),

    // Personnummer: krävs om BankID-vald, annars valfritt men måste matcha om ifyllt
    personalIdentityNumber: yup
      .string()
      .transform((v) => (typeof v === 'string' ? v.trim() : v))
      .when('bankIdLoginEnabled', {
        is: true,
        then: (s) => s.required(requiredMessage).matches(personNrFormat, personnummerFormatMessage),
        otherwise: (s) =>
          s.notRequired().matches(personNrFormat, {
            message: personnummerFormatMessage,
            excludeEmptyString: true,
          }),
      }),
  });

  const formMethods = useForm<UserRequest>({
    resolver: yupResolver(CreateUserSchema),
    mode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      comment: '',
      personalIdentityNumber: '',
      emailLoginEnabled: false,
      bankIdLoginEnabled: false,
      districts: [],
      associations: [],
      sysProducts: [],
      invoiceGroups: [],
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = formMethods;

  const emailVal = watch('emailLoginEnabled');
  const bankVal = watch('bankIdLoginEnabled');

  useEffect(() => {
    trigger(['emailLoginEnabled', 'bankIdLoginEnabled']);
  }, [emailVal, bankVal, trigger]);

  const { fields: sysProductFields, append: appendSysProduct } = useFieldArray({
    control,
    name: 'sysProducts',
  });

  useFieldArray({
    control,
    name: 'invoiceGroups',
  });

  const onSubmit = useCallback(
    async (data: UserRequest) => {
      setLoading(true);
      try {
        const requestData = {
          ...data,
          user_id: userId,
          sysProducts: data.sysProducts
            .filter((x) => x.isChecked)
            .map((x) => {
              return { product_id: x.product_id, authority: x.authority };
            }),
        };
        await updateUser(requestData);
        if (isMounted()) {
          toast.success(<>{t('user:createUser.success')}</>);
          ConsoleHelper.log(requestData);
          onHandleSave();
          // navigate(`${RoutesEnum.InvoicesDetail}/${result.data}`);
        }
      } catch (e: any) {
        if (isMounted()) {
          const errorMessage = e?.data?.messages;
          toast.error(<>{errorMessage}</>);
        }
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, onHandleSave, t, userId],
  );

  const handleSubmitClick = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingUser(true);
      try {
        const result = await getUserById(userId);
        if (isMounted()) {
          setUser(result.data);
          setValue('firstName', result.data.firstName);
          setValue('lastName', result.data.lastName);
          setValue('email', result.data.email);
          setValue('comment', result.data.comment);
          setValue('personalIdentityNumber', result.data.personalIdentityNumber ?? '');
          setValue('emailLoginEnabled', result.data.emailLoginEnabled ?? false);
          setValue('bankIdLoginEnabled', result.data.bankIdLoginEnabled ?? false);

          if (result.data?.districts?.length > 0) {
            setValue('districts', result.data.districts as import('src/shared/models/district/district').District[]);
          }
          if (result.data?.associations?.length > 0) {
            setValue('associations', result.data.associations);
          }
          if (result.data?.invoiceGroups?.length > 0) {
            setValue('invoiceGroups', result.data.invoiceGroups);
            setInitialInvoiceGroup(result.data.invoiceGroups);
          }
          sysProducts.forEach((item, index) => {
            const found = result.data.sysProducts?.find((x) => x.product_id === item.product_id);
            if (found) {
              setValue(`sysProducts.${index}.isChecked`, true);
              setValue(`sysProducts.${index}.authority`, found.authority);
            }
          });
          setTimeout(() => {
            if (isMounted()) setLoadingUser(false);
          }, 500);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          setLoadingUser(false);
        }
      }
    };
    if (userId && sysProducts.length > 0) fetchData();
  }, [isMounted, reset, setValue, sysProducts, sysProducts.length, userId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getAllSysProducts();
        if (isMounted()) {
          if (result.data.results) {
            // sortera sysProducts efter api-response: currentUser-sysProducts
            const currentSysProductNames = currentUser?.sysProducts?.map((product) => product.yvName) || [];
            const sortedSysProducts = result.data.results.sort((a, b) => {
              const indexA = currentSysProductNames.indexOf(a.yvName);
              const indexB = currentSysProductNames.indexOf(b.yvName);
              return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            });

            setSysProducts(sortedSysProducts);
            setSysProductLoaded(true);

            if (!(sysProductFields.length > 0)) {
              result.data.results.forEach((item) => {
                appendSysProduct({ product_id: item.product_id, authority: 0, isChecked: false, yvName: item.yvName });
              });
            }

            const findAuthority = currentUser?.sysProducts.find((item) => item.yvName.toLowerCase() === SysProductYvNameEnum.System.toLowerCase());
            if (findAuthority) {
              setAuthority(findAuthority?.authority);
            }
          }
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
        }
      }
    };
    if (!sysProductLoaded) fetchData();
  }, [appendSysProduct, currentUser?.sysProducts, isMounted, sysProductFields.length, sysProductLoaded]);

  useEffect(() => {
    if (!currentUser?.sysProducts || sysProducts.length === 0) return;
    const currentSysProductNames = currentUser.sysProducts.map((product) => product.yvName);

    // Filtrera ut moduler från sysProducts som inte finns i currentUser.sysProducts
    const disabledProducts = sysProducts.filter((product) => !currentSysProductNames.includes(product.yvName));

    setDisableSysProducts(disabledProducts);
  }, [currentUser, sysProducts]);

  const handleDelete = () => {
    (async () => {
      setLoadingUser(true);
      try {
        await deleteUserById(userId);
        if (isMounted()) {
          toast.success(<>{t('user:editUser.deleteSuccess')}</>);
          onHandleSave();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoadingUser(false);
      }
    })();
  };

  const invoiceIsChecked = () => {
    const form = watch('sysProducts') || [];
    const foundItem = form.find((item) => item.yvName?.toLowerCase() === SysProductYvNameEnum.Invoice.toLowerCase());
    return foundItem ? foundItem.isChecked : false;
  };

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogTitle={t('user:editUser.modalHeader')}
      showDialogActions
      maxWidth='lg'
      padding={0}
      dialogContent={
        <FormLoaderWrapper loading={loading || loadingUser}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ p: 3 }}>
                <GridWrapper>
                  <Grid item xs={12} md={6}>
                    <GridWrapper>
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        <ControlledTextField
                          required
                          name='email'
                          label={t('user:createUser.email')}
                          disabled
                          helperText={
                            <Box sx={{ display: 'flex', alignItems: 'center' }} component='span'>
                              <InfoRounded sx={{ fontSize: '0.8rem', mr: 0.5, mb: '3px', color: Colors.ChipColors.Blue }} />
                              <span>{t('user:createUser.emailDescription')}</span>
                            </Box>
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <ControlledTextField required name='firstName' label={t('user:createUser.firstName')} />
                      </Grid>
                      <Grid item xs={6}>
                        <ControlledTextField required name='lastName' label={t('user:createUser.lastName')} />
                      </Grid>

                      <Grid item xs={12}>
                        <ControlledTextField name='personalIdentityNumber' label={t('user:createUser.personalIdentityNumber')} />
                      </Grid>

                      <Grid item xs={12} mb={1}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent='space-between'
                          alignItems='flex-start'
                          spacing={2}
                          sx={{ width: '100%' }}
                        >
                          <Typography sx={{ whiteSpace: 'nowrap' }}>{`${t('user:createUser.loginMethods')}*`}</Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: { xs: 'flex-start', sm: 'flex-end' },
                              minWidth: 0,
                            }}
                          >
                            <FormGroup
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                mb: -1,
                              }}
                            >
                              <ControlledCheckboxNew name='emailLoginEnabled' label={t('user:createUser.emailLoginEnabled')} />
                              <ControlledCheckboxNew name='bankIdLoginEnabled' label={t('user:createUser.bankIdLoginEnabled')} />
                            </FormGroup>

                            <FormHelperText error>
                              {errors.emailLoginEnabled?.message || errors.bankIdLoginEnabled?.message || errors?.root?.message || ' '}
                            </FormHelperText>
                          </Box>
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <ControlledTextField
                          name='comment'
                          multiline
                          rows={4}
                          label={t('user:createUser.comment')}
                          inputProps={{ maxLength: USER_COMMENT_MAX_LENGTH }}
                        />
                        <FormCharacterCount name='comment' />
                      </Grid>
                      {user && (
                        <Grid item xs={12}>
                          <UserEditMembershipSummary
                            user={user}
                            setShowDistrictDropdown={setShowDistrictDropdown}
                            setShowAssociationDropdown={setShowAssociationDropdown}
                            showAssociationDropdown={showAssociationDropdown}
                            showDistrictDropdown={showDistrictDropdown}
                          />
                        </Grid>
                      )}
                      {invoiceIsChecked() && (
                        <Grid item xs={12}>
                          <UserEditInvoiceGroupSummary initialInvoiceGroup={initialInvoiceGroup || []} />
                        </Grid>
                      )}
                    </GridWrapper>
                  </Grid>
                  <Divider orientation='vertical' flexItem sx={{ ml: 3, mt: 3, display: { sm: 'none', md: 'flex' } }} />
                  <Grid item xs={12} md>
                    <Typography variant='caption'>{t('user:createUser.permissionHeader')}</Typography>
                    {sysProductFields &&
                      sysProductFields.map((item, index) => {
                        const sysProd = sysProducts
                          .filter((x) => !excludedSysProducts.includes(x.yvName.toLowerCase()))
                          .find((x) => x.product_id === item.product_id);
                        return (
                          <UserCreateModalPermissionButton
                            sysProduct={sysProd}
                            key={item.product_id}
                            index={index}
                            authorityStatus={authority}
                            disableSysProducts={disableSysProducts}
                          />
                        );
                      })}
                    <Box mt={2}>
                      <FormErrorSummary translationNamespace='user' errors={errors} />
                    </Box>
                  </Grid>
                </GridWrapper>
              </Box>
            </form>
          </FormProvider>
        </FormLoaderWrapper>
      }
      dialogActions={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <UserEditConfirmDelete onAbort={() => ConsoleHelper.log('')} onConfirm={() => handleDelete()} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant='outlined' sx={{ mr: 1 }} onClick={onHandleClose}>
              {t('common:button.cancel')}
            </Button>
            <LoadingButton loading={loading} type='submit' variant='contained' disableElevation onClick={handleSubmitClick}>
              {t('common:button.save')}
            </LoadingButton>
          </Box>
        </Box>
      }
    />
  );
};
